import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// ✅ Fetch incomes
export const useIncomes = (token: string | null) => {
  return useQuery({
    queryKey: ['incomes', token],
    queryFn: async () => {
      if (!token) throw new Error('Authentication required');
      
      try {
        const response = await axios.get(`${API_URL}/incomes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }
        if (error.response?.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }
        if (!navigator.onLine) {
          throw new Error('No internet connection.');
        }
        throw new Error(error.response?.data?.error || 'Failed to load incomes');
      }
    },
    enabled: !!token,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
    retry: 3, // ✅ Add retry
  });
};


// ✅ Create income
export const useCreateIncome = (token: string | null) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (incomeData: any) => {
      if (!token) throw new Error('No token provided');
      const response = await axios.post(`${API_URL}/incomes`, incomeData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: () => {
      // ✅ Invalidate BOTH caches since incomes affect settings
      queryClient.invalidateQueries({ queryKey: ['incomes', token] });
      queryClient.invalidateQueries({ queryKey: ['settings', token] });
    },
  });
};

// ✅ Update income
export const useUpdateIncome = (token: string | null) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      if (!token) throw new Error('No token provided');
      const response = await axios.put(`${API_URL}/incomes/${id}`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: () => {
      // ✅ Invalidate BOTH caches
      queryClient.invalidateQueries({ queryKey: ['incomes', token] });
      queryClient.invalidateQueries({ queryKey: ['settings', token] });
    },
  });
};

// ✅ Delete income
export const useDeleteIncome = (token: string | null) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error('No token provided');
      const response = await axios.delete(`${API_URL}/incomes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: () => {
      // ✅ Invalidate BOTH caches
      queryClient.invalidateQueries({ queryKey: ['incomes', token] });
      queryClient.invalidateQueries({ queryKey: ['settings', token] });
    },
  });
};

// ============  Documentation ============
/**
 * @fileoverview Custom React Query hooks for income source management
 * @module hooks/useIncomes
 * 
 * This module provides hooks for managing multiple income sources:
 * - Fetching all income sources
 * - Creating new income sources (salary, freelance, gig work, etc.)
 * - Updating existing income sources
 * - Deleting income sources
 * 
 * Income sources are used to categorize expenses and track which paycheck
 * each expense comes from. Supports different payment frequencies:
 * - Weekly (4 waves per month)
 * - Biweekly (2 waves per month)
 * - Monthly (1 wave per month)
 * - One-time (single payment)
 * 
 * Note: When income sources are modified, both incomes and settings caches
 * are invalidated since settings contain income source configuration.
 */

/**
 * Hook to fetch all income sources for the authenticated user
 * 
 * @param {string | null} token - JWT authentication token
 * 
 * @returns {UseQueryResult} React Query result object containing:
 * - data: Array<{
 *     _id: string,
 *     name: string,
 *     amount: number,
 *     frequency: 'weekly' | 'biweekly' | 'monthly' | 'one-time',
 *     nextDate?: string
 *   }>
 * - isLoading: boolean - Loading state
 * - error: Error | null - Error object if request failed
 * 
 * @example
 * const { data: incomes, isLoading } = useIncomes(token);
 * if (incomes) {
 *   incomes.forEach(income => {
 *     console.log(`${income.name}: $${income.amount} (${income.frequency})`);
 *   });
 * }
 * 
 * @features
 * - Automatic retry (3 attempts)
 * - Caching (stale after 2 minutes)
 * - Better error messages
 * - Offline detection
 */

/**
 * Hook to create a new income source
 * 
 * @param {string | null} token - JWT authentication token
 * 
 * @returns {UseMutationResult} React Query mutation object
 * 
 * @example
 * const createIncome = useCreateIncome(token);
 * 
 * createIncome.mutate({
 *   name: "Freelance Work",
 *   amount: 2000,
 *   frequency: "monthly",
 *   nextDate: "2025-02-01"
 * }, {
 *   onSuccess: () => console.log("Income source added!"),
 *   onError: (error) => console.error(error)
 * });
 * 
 * @features
 * - Invalidates both incomes and settings caches
 * - Settings cache updated because it contains income sources config
 */

/**
 * Hook to update an existing income source
 * 
 * @param {string | null} token - JWT authentication token
 * 
 * @returns {UseMutationResult} React Query mutation object
 * 
 * @example
 * const updateIncome = useUpdateIncome(token);
 * 
 * updateIncome.mutate({
 *   id: "income-123",
 *   updates: {
 *     amount: 2500,
 *     nextDate: "2025-02-15"
 *   }
 * }, {
 *   onSuccess: () => console.log("Income updated!"),
 *   onError: (error) => console.error(error)
 * });
 * 
 * @features
 * - Invalidates both incomes and settings caches
 * - Updates reflected in expense categorization
 */

/**
 * Hook to delete an income source
 * 
 * @param {string | null} token - JWT authentication token
 * 
 * @returns {UseMutationResult} React Query mutation object
 * 
 * @example
 * const deleteIncome = useDeleteIncome(token);
 * 
 * deleteIncome.mutate("income-123", {
 *   onSuccess: () => console.log("Income source deleted!"),
 *   onError: (error) => console.error(error)
 * });
 * 
 * @warning
 * Deleting an income source will affect expenses categorized under it.
 * Consider reassigning expenses before deletion.
 * 
 * @features
 * - Invalidates both incomes and settings caches
 * - Removes income source from all linked expenses
 */
